export interface FormatDateOptions {
  day?: '2-digit' | 'numeric'
  month?: 'short' | 'long' | 'numeric' | '2-digit'
  year?: 'numeric' | '2-digit'
  hour?: '2-digit' | 'numeric'
  minute?: '2-digit' | 'numeric'
}

const defaultOptions: FormatDateOptions = {
  day: '2-digit',
  month: 'short',
  year: 'numeric',
}

const defaultDateTimeOptions: FormatDateOptions = { ...defaultOptions, hour: '2-digit', minute: '2-digit' }

/**
 * One formatter per locale and options, kept: `toLocaleDateString` with options builds a new
 * `Intl.DateTimeFormat` on every call, which a list formatting a date per row pays per row.
 */
const formatters = new Map<string, Intl.DateTimeFormat>()

function formatterFor(locale: string, options: FormatDateOptions): Intl.DateTimeFormat {
  const key = `${locale}|${options.day}|${options.month}|${options.year}|${options.hour}|${options.minute}`
  let formatter = formatters.get(key)
  if (!formatter) {
    formatter = new Intl.DateTimeFormat(locale, options)
    formatters.set(key, formatter)
  }
  return formatter
}

export function formatDate(
  dateString: string | Date,
  locale: string = 'en',
  options: FormatDateOptions = defaultOptions,
): string {
  const date = typeof dateString === 'string' ? new Date(dateString) : dateString
  return formatterFor(locale, options).format(date)
}

/**
 * A calendar day the server answers as `YYYY-MM-DD` — an issue date, a day a meter counted — read
 * as that day wherever the browser is. `formatDate` reads such a string as midnight UTC, which a
 * browser west of Greenwich formats as the day before.
 */
export function formatCalendarDay(
  day: string,
  locale: string = 'en',
  options: FormatDateOptions = defaultOptions,
): string {
  const parts = /^(\d{4})-(\d{2})-(\d{2})$/.exec(day)
  const date = parts ? new Date(Number(parts[1]), Number(parts[2]) - 1, Number(parts[3])) : new Date(day)
  return formatterFor(locale, options).format(date)
}

export function formatDateTime(
  dateString: string | Date,
  locale: string = 'en',
  options: FormatDateOptions = defaultDateTimeOptions,
): string {
  const date = typeof dateString === 'string' ? new Date(dateString) : dateString
  return formatterFor(locale, options).format(date)
}

export function formatRelativeTime(dateString: string | Date, locale: string = 'en'): string {
  const date = typeof dateString === 'string' ? new Date(dateString) : dateString
  const now = new Date()
  const diffMs = now.getTime() - date.getTime()
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24))

  if (diffDays === 0) {
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60))
    if (diffHours === 0) {
      const diffMinutes = Math.floor(diffMs / (1000 * 60))
      if (diffMinutes < 1) return locale === 'fr' ? 'À l\'instant' : 'Just now'
      return locale === 'fr' ? `Il y a ${diffMinutes} min` : `${diffMinutes}m ago`
    }
    return locale === 'fr' ? `Il y a ${diffHours}h` : `${diffHours}h ago`
  }

  if (diffDays === 1) return locale === 'fr' ? 'Hier' : 'Yesterday'
  if (diffDays < 7) return locale === 'fr' ? `Il y a ${diffDays} jours` : `${diffDays}d ago`

  return formatDate(date, locale)
}
